import { apiFetch } from '../api/httpClient.ts';

/**
 * The country Cloudflare thinks this request came from.
 *
 * `CF-IPCountry` is a *request* header — the browser cannot read the headers it
 * sends, so a server-side echo is the only way the client can learn it. nginx
 * serves that echo directly (design §5.1); no backend, no DB, no cache.
 *
 * This only picks which row starts highlighted. The country that gets written is
 * derived from the prefix of the phone number the player actually verified, so a
 * wrong guess here costs a tap, never an account.
 */

/** Cloudflare's non-country markers: unknown, and Tor exit. */
const NOT_A_COUNTRY = new Set(['XX', 'T1']);

/** Parse the nginx payload. Anything unexpected is "no suggestion", never a throw. */
export function parseGeoSuggestion(payload: unknown): string | null {
  const raw = (payload as { code?: unknown })?.code;
  const code = typeof raw === 'string' ? raw.trim().toUpperCase() : '';
  if (!/^[A-Z]{2}$/.test(code)) return null;
  return NOT_A_COUNTRY.has(code) ? null : code;
}

/**
 * Never rejects. A suggestion is a nicety — a failed lookup must not be visible on
 * the registration screen, let alone block it.
 */
export async function fetchCountrySuggestion(): Promise<string | null> {
  try {
    return parseGeoSuggestion(await apiFetch<unknown>('/api/v1/geo/country', { method: 'GET' }));
  } catch {
    return null;
  }
}
